import { ChevronDown, Settings2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Select, SwitchField } from '@/components/ui/controls'
import { Field, Input, Textarea } from '@/components/ui/field'
import { emptyTemplate, type TemplateDraft } from '@/data/templates'
import type { NoteTemplate, ReasoningEffort } from '@/data/types'
import { cn } from '@/lib/utils'

const EFFORTS: { value: ReasoningEffort; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'xhigh', label: 'Extra high' },
]

function toDraft(template: NoteTemplate): TemplateDraft {
  const { id: _id, isSystemDefault: _system, updatedAt: _updated, ...draft } = template
  return draft
}

/**
 * The editor for one template.
 *
 * Instructions get the room, because instructions ARE the template — everything
 * else is either a label for humans or a knob on how the instruction runs. The
 * knobs are behind a disclosure for that reason and not to save space: a model
 * name and a token ceiling beside the name field imply they are part of the
 * decision being made, and they almost never are.
 *
 * Uncontrolled from the parent's point of view: the draft is local and only
 * leaves on save, so cancelling is genuinely free and a half-typed instruction
 * is never written anywhere.
 */
export function TemplateEditor({
  template,
  busy,
  onSave,
  onCancel,
}: {
  /** Undefined creates. */
  template?: NoteTemplate
  busy?: boolean
  onSave: (draft: TemplateDraft) => void
  onCancel: () => void
}) {
  const [draft, setDraft] = useState<TemplateDraft>(() =>
    template ? toDraft(template) : emptyTemplate()
  )
  const [advanced, setAdvanced] = useState(false)
  const [touched, setTouched] = useState(false)

  // Editing a different row while one is open replaces the draft rather than
  // carrying the previous template's text into it.
  useEffect(() => {
    setDraft(template ? toDraft(template) : emptyTemplate())
    setAdvanced(false)
    setTouched(false)
  }, [template])

  const set = <K extends keyof TemplateDraft>(key: K, value: TemplateDraft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }))

  const nameError = touched && !draft.name.trim() ? 'A name is required.' : null
  const instructionsError =
    touched && !draft.instructions.trim() ? 'Instructions are required.' : null

  function save() {
    setTouched(true)
    if (!draft.name.trim() || !draft.instructions.trim()) return
    onSave({ ...draft, name: draft.name.trim(), instructions: draft.instructions.trim() })
  }

  return (
    <div className="space-y-4 border-t border-[var(--color-border)] px-3 py-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Name" required error={nameError}>
          {(props) => (
            <Input
              {...props}
              autoFocus
              value={draft.name}
              maxLength={80}
              placeholder="Action items"
              className="h-8 text-xs"
              onChange={(event) => set('name', event.target.value)}
            />
          )}
        </Field>

        <Field label="Description" hint="Shown wherever this template is offered.">
          {(props) => (
            <Input
              {...props}
              value={draft.description}
              maxLength={120}
              placeholder="Owners, deadlines and open commitments"
              className="h-8 text-xs"
              onChange={(event) => set('description', event.target.value)}
            />
          )}
        </Field>
      </div>

      <Field
        label="Instructions"
        required
        error={instructionsError}
        hint="The transcript and the participant names are supplied automatically. Results are Markdown."
      >
        {(props) => (
          <Textarea
            {...props}
            rows={12}
            value={draft.instructions}
            maxLength={8000}
            placeholder="Describe what to identify, and how the result should be organised."
            className="text-xs leading-relaxed"
            onChange={(event) => set('instructions', event.target.value)}
          />
        )}
      </Field>

      <div className="divide-y divide-[var(--color-border-subtle)] rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3">
        <SwitchField
          label="Write it automatically"
          hint="Runs on every meeting that gets recorded, without being asked for."
          checked={draft.autoGenerate}
          onCheckedChange={(value) => set('autoGenerate', value)}
        />
        <SwitchField
          label="Share externally by default"
          hint="New notes from this template start visible to external participants."
          checked={draft.shareByDefault}
          onCheckedChange={(value) => set('shareByDefault', value)}
        />
      </div>

      <div>
        <button
          type="button"
          onClick={() => setAdvanced((value) => !value)}
          aria-expanded={advanced}
          className="flex h-7 w-full items-center gap-1.5 text-2xs text-[var(--color-fg-muted)] outline-none hover:text-[var(--color-fg)]"
        >
          <Settings2 className="size-3" />
          <span className="flex-1 text-left">How it runs</span>
          <ChevronDown className={cn('size-3 transition-transform', advanced && 'rotate-180')} />
        </button>

        {advanced ? (
          <div className="grid gap-3 pt-2 sm:grid-cols-4">
            <Field label="Model">
              {(props) => (
                <Input
                  {...props}
                  value={draft.model}
                  className="h-8 font-mono text-xs"
                  onChange={(event) => set('model', event.target.value)}
                />
              )}
            </Field>
            <Field label="Max output tokens">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  min={1}
                  value={draft.maxTokens}
                  className="tnum h-8 text-xs"
                  onChange={(event) =>
                    set('maxTokens', Number.parseInt(event.target.value, 10) || 15_000)
                  }
                />
              )}
            </Field>
            <Field label="Reasoning effort">
              {(props) => (
                <Select
                  id={props.id}
                  size="sm"
                  value={draft.reasoningEffort}
                  onValueChange={(value) => set('reasoningEffort', value)}
                  options={EFFORTS}
                />
              )}
            </Field>
            <Field label="Order" hint="Lower sorts first.">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  value={draft.displayOrder}
                  className="tnum h-8 text-xs"
                  onChange={(event) =>
                    set('displayOrder', Number.parseInt(event.target.value, 10) || 0)
                  }
                />
              )}
            </Field>
          </div>
        ) : null}
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" loading={busy} onClick={save}>
          {template ? 'Save changes' : 'Create template'}
        </Button>
      </div>
    </div>
  )
}
